import React, { useMemo } from 'react';
import {
  AdminBreadcrumbsTw,
  AdminDevRowMeta,
  btnLink,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  inputClass,
} from '../adminUi';
import { useGrading } from './GradingContext';
import {
  GradingActionsMenu,
  GradingColumnHeader,
  GradingContextPath,
  GradingEmptyState,
  GradingHeaderActions,
  GradingMetaLine,
  GradingScoreRange,
  GradingTypeBadge,
  GradingVisibilityBadge,
} from './GradingShared';
import {
  countQuestionsForFamily,
  countQuestionsForRoleCluster,
  countRolesForFamily,
  confirmAndRunDelete,
  formatPath,
  getCalibrationForFamily,
} from './utils';
import {
  deleteGradingFamily,
  deleteGradingRole,
  deleteGradingRoleCluster,
} from '../../../../services/gradingService';

export default function GradingTreeTab() {
  const {
    families,
    roleClusters,
    specializations,
    roles,
    factors,
    questions,
    calibrationQuestions,
    calibrationAnswers,
    selectedFamilyId,
    setSelectedFamilyId,
    treeSelection,
    setTreeSelection,
    expandedNodes,
    toggleNode,
    search,
    setSearch,
    openDrawer,
    setTab,
    reload,
  } = useGrading();

  const filteredFamilies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return families;
    return families.filter((f) => {
      if (f.name.toLowerCase().includes(q)) return true;
      const clusters = roleClusters.filter((s) => s.family_id === f.id);
      if (clusters.some((s) => s.name.toLowerCase().includes(q))) return true;
      const specs = specializations.filter((s) => s.family_id === f.id);
      if (specs.some((s) => s.name.toLowerCase().includes(q))) return true;
      const clusterIds = clusters.map((s) => s.id);
      return roles.some(
        (r) => clusterIds.includes(r.role_cluster_id) && (r.name.toLowerCase().includes(q) || String(r.grade_level).includes(q)),
      );
    });
  }, [families, roleClusters, specializations, roles, search]);

  const selectedFamily = families.find((f) => f.id === selectedFamilyId) ?? null;
  const familyRoleClusters = roleClusters.filter((s) => s.family_id === selectedFamilyId);
  const familySpecs = specializations.filter((s) => s.family_id === selectedFamilyId);

  const breadcrumbs = useMemo(() => {
    const items = [{ label: 'Грейдинг' }, { label: 'Дерево грейдинга' }];
    if (!treeSelection) return items;
    if (treeSelection.kind === 'family') {
      const f = families.find((x) => x.id === treeSelection.id);
      if (f) items.push({ label: f.name });
    }
    if (treeSelection.kind === 'role_cluster') {
      const rc = roleClusters.find((x) => x.id === treeSelection.id);
      const f = rc ? families.find((x) => x.id === rc.family_id) : undefined;
      if (f) items.push({ label: f.name });
      if (rc) items.push({ label: rc.name });
    }
    if (treeSelection.kind === 'role') {
      const r = roles.find((x) => x.id === treeSelection.id);
      const rc = r ? roleClusters.find((x) => x.id === r.role_cluster_id) : undefined;
      const f = rc ? families.find((x) => x.id === rc.family_id) : undefined;
      const sp = r?.specialization_id ? specializations.find((x) => x.id === r.specialization_id) : undefined;
      if (f) items.push({ label: f.name });
      if (rc) items.push({ label: rc.name });
      if (sp) items.push({ label: sp.name });
      if (r) items.push({ label: `${r.name}, грейд ${r.grade_level}` });
    }
    return items;
  }, [treeSelection, families, roleClusters, specializations, roles]);

  const selectFamily = (id: number) => {
    setSelectedFamilyId(id);
    setTreeSelection({ kind: 'family', id });
  };

  const renderDetails = () => {
    if (!treeSelection) {
      return <GradingEmptyState>Выберите элемент дерева для просмотра деталей</GradingEmptyState>;
    }

    if (treeSelection.kind === 'family') {
      const f = families.find((x) => x.id === treeSelection.id);
      if (!f) return null;
      const roleClusterCount = roleClusters.filter((s) => s.family_id === f.id).length;
      const roleCount = countRolesForFamily(f.id, roleClusters, roles);
      const qCount = countQuestionsForFamily(f.id, roleClusters, factors, questions);
      const specs = specializations.filter((s) => s.family_id === f.id);
      const calib = getCalibrationForFamily(f.id, calibrationQuestions, calibrationAnswers);
      return (
        <div className="dc-grading-details">
          <div className="dc-grading-details-head">
            <h4>{f.name}</h4>
            <div className="flex items-center gap-2">
              <GradingVisibilityBadge isVisible={f.is_visible} />
              <GradingTypeBadge label="Family" variant="neutral" />
            </div>
          </div>
          <AdminDevRowMeta parts={[`families.id=${f.id}`]} />
          <GradingMetaLine items={[`${roleClusterCount} role cluster`, `${specs.length} specialization`, `${roleCount} roles`, `${qCount} вопросов`]} />
          {calib.question && (
            <div className="dc-grading-details-block">
              <span className="dc-grading-details-label">Калибровка</span>
              <p className="text-sm text-slate-700">{calib.question.question_text}</p>
              <p className="text-xs text-slate-500">{calib.answers.length} ответов → role cluster</p>
            </div>
          )}
          {specs.length > 0 && (
            <div className="dc-grading-details-block">
              <span className="dc-grading-details-label">Specializations</span>
              <ul className="dc-grading-list">
                {specs.map((s) => <li key={s.id}>{s.name}</li>)}
              </ul>
            </div>
          )}
          <div className="dc-grading-details-actions">
            <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'family', mode: 'edit', id: f.id })}>
              Редактировать Family
            </button>
            <button type="button" className={btnSecondary} onClick={() => openDrawer({ type: 'role_cluster', mode: 'create', familyId: f.id })}>
              Добавить Role cluster
            </button>
            <button type="button" className={btnSecondary} onClick={() => openDrawer({ type: 'specialization', mode: 'create', familyId: f.id })}>
              Добавить Specialization
            </button>
            <button type="button" className={btnSecondary} onClick={() => openDrawer({ type: 'calibration', mode: 'edit', familyId: f.id })}>
              Настроить калибровку
            </button>
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-800"
              onClick={() => confirmAndRunDelete(
                f.name,
                'Будут удалены все role cluster, roles, вопросы, диффакторы и калибровка этой Family.',
                () => deleteGradingFamily(f.id),
                reload,
              )}
            >
              Удалить Family
            </button>
          </div>
        </div>
      );
    }

    if (treeSelection.kind === 'role_cluster') {
      const rc = roleClusters.find((x) => x.id === treeSelection.id);
      if (!rc) return null;
      const f = families.find((x) => x.id === rc.family_id);
      const rcRoles = roles.filter((r) => r.role_cluster_id === rc.id);
      const qCount = countQuestionsForRoleCluster(rc.id, rc.family_id, factors, questions);
      return (
        <div className="dc-grading-details">
          <div className="dc-grading-details-head">
            <h4>{rc.name}</h4>
            <GradingTypeBadge label="Role cluster" variant="calibration" />
          </div>
          <GradingContextPath path={formatPath([f?.name, rc.name])} />
          <AdminDevRowMeta parts={[`role_clusters.id=${rc.id}`, `role_clusters.family_id=${rc.family_id}`]} />
          <GradingMetaLine items={[`${rcRoles.length} roles`, `${qCount} scoring-вопросов`]} />
          <div className="dc-grading-details-block">
            <span className="dc-grading-details-label">Roles</span>
            <ul className="dc-grading-list">
              {rcRoles.map((r) => {
                const sp = r.specialization_id ? specializations.find((s) => s.id === r.specialization_id) : undefined;
                return (
                  <li key={r.id}>
                    {r.name}, грейд {r.grade_level}{sp ? ` (${sp.name})` : ''}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="dc-grading-details-actions">
            <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'role_cluster', mode: 'edit', id: rc.id })}>
              Редактировать Role cluster
            </button>
            <button type="button" className={btnSecondary} onClick={() => openDrawer({ type: 'role', mode: 'create', roleClusterId: rc.id, familyId: f?.id })}>
              Добавить Role
            </button>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => openDrawer({ type: 'question', mode: 'create', context: { familyId: f?.id, roleClusterId: rc.id } })}
            >
              Добавить вопрос
            </button>
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-800"
              onClick={() => confirmAndRunDelete(
                rc.name,
                'Будут удалены roles и scoring-вопросы этой Role cluster.',
                () => deleteGradingRoleCluster(rc.id),
                reload,
              )}
            >
              Удалить Role cluster
            </button>
          </div>
        </div>
      );
    }

    if (treeSelection.kind === 'role') {
      const r = roles.find((x) => x.id === treeSelection.id);
      if (!r) return null;
      const rc = roleClusters.find((x) => x.id === r.role_cluster_id);
      const f = rc ? families.find((x) => x.id === rc.family_id) : undefined;
      const sp = r.specialization_id ? specializations.find((x) => x.id === r.specialization_id) : undefined;
      const roleQuestions = questions.filter((q) => q.role_cluster_id === r.role_cluster_id);
      return (
        <div className="dc-grading-details">
          <div className="dc-grading-details-head">
            <h4>{r.name}</h4>
            <GradingTypeBadge label="Role" variant="scoring" />
          </div>
          <GradingContextPath path={formatPath([f?.name, rc?.name, sp?.name, `грейд ${r.grade_level}`])} />
          <AdminDevRowMeta parts={[
            `roles.id=${r.id}`,
            `roles.role_cluster_id=${r.role_cluster_id}`,
            r.specialization_id != null && `roles.specialization_id=${r.specialization_id}`,
            r.min_score != null && `roles.min_score=${r.min_score}`,
            r.max_score != null && `roles.max_score=${r.max_score}`,
          ]} />
          <div className="dc-grading-details-grid">
            <div><span className="dc-grading-details-label">Grade</span><p>{r.grade_level}</p></div>
            <div><span className="dc-grading-details-label">Specialization</span><p>{sp?.name || '—'}</p></div>
            <div><span className="dc-grading-details-label">Диапазон баллов</span><p><GradingScoreRange min={r.min_score} max={r.max_score} /></p></div>
          </div>
          {r.description && <p className="text-sm text-slate-600">{r.description}</p>}
          {roleQuestions.length > 0 && (
            <div className="dc-grading-details-block">
              <span className="dc-grading-details-label">Вопросы role cluster</span>
              <ul className="dc-grading-list">
                {roleQuestions.map((q) => (
                  <li key={q.id}>{q.question_text}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="dc-grading-details-actions">
            <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'role', mode: 'edit', id: r.id })}>
              Редактировать Role
            </button>
            <button type="button" className={btnSecondary} onClick={() => setTab('roles')}>
              Открыть настройки баллов
            </button>
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-800"
              onClick={() => confirmAndRunDelete(
                r.name,
                'Role будет удалена без возможности восстановления.',
                () => deleteGradingRole(r.id),
                reload,
              )}
            >
              Удалить Role
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dc-grading-tree-page">
      <AdminBreadcrumbsTw items={breadcrumbs} />

      <div className="dc-grading-page-header">
        <div>
          <h2 className="dc-grading-title">Грейдинг</h2>
          <p className="dc-grading-subtitle">
            Family → калибровка → Role cluster → scoring → Role + grade
          </p>
        </div>
        <GradingHeaderActions
          primaryLabel="Создать Family"
          onPrimary={() => openDrawer({ type: 'family', mode: 'create' })}
          secondaryLabel="Предпросмотр анкеты"
          onSecondary={() => setTab('preview')}
        />
      </div>

      <div className={`${filtersRowClass} mb-4`}>
        <input
          className={inputClass}
          placeholder="Поиск по family, role cluster, role или грейду"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="dc-grading-three-col">
        <section className="dc-grading-col">
          <GradingColumnHeader title="Family" />
          <div className="dc-grading-col-body">
            {filteredFamilies.map((f) => {
              const roleClusterCount = roleClusters.filter((s) => s.family_id === f.id).length;
              const roleCount = countRolesForFamily(f.id, roleClusters, roles);
              const qCount = countQuestionsForFamily(f.id, roleClusters, factors, questions);
              const active = selectedFamilyId === f.id;
              return (
                <div
                  key={f.id}
                  className={`dc-grading-family-card ${active ? 'dc-grading-family-card-active' : ''}`}
                  onClick={() => selectFamily(f.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && selectFamily(f.id)}
                >
                  <div className="dc-grading-family-card-head">
                    <strong>{f.name}</strong>
                    <GradingVisibilityBadge isVisible={f.is_visible} />
                  </div>
                  <AdminDevRowMeta parts={[`families.id=${f.id}`]} />
                  <GradingMetaLine items={[`${roleClusterCount} role cluster`, `${roleCount} roles`, `${qCount} вопросов`]} />
                  <GradingActionsMenu
                    onEdit={() => openDrawer({ type: 'family', mode: 'edit', id: f.id })}
                    onDelete={() => confirmAndRunDelete(
                      f.name,
                      'Будут удалены все role cluster, roles, вопросы, диффакторы и калибровка этой Family.',
                      () => deleteGradingFamily(f.id),
                      reload,
                    )}
                  />
                </div>
              );
            })}
          </div>
        </section>

        <section className="dc-grading-col">
          <GradingColumnHeader
            title={selectedFamily ? selectedFamily.name : 'Role cluster и Role'}
            action={
              selectedFamily ? (
                <button type="button" className={btnSecondary} onClick={() => openDrawer({ type: 'role_cluster', mode: 'create', familyId: selectedFamily.id })}>
                  + Role cluster
                </button>
              ) : undefined
            }
          />
          <div className="dc-grading-col-body">
            {!selectedFamily ? (
              <GradingEmptyState>Выберите Family слева</GradingEmptyState>
            ) : (
              <>
                {familySpecs.length > 0 && (
                  <div className="mb-4 pb-3 border-b border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2">Specializations (family-level)</p>
                    <div className="flex flex-wrap gap-2">
                      {familySpecs.map((sp) => (
                        <span key={sp.id} className="inline-flex flex-col items-start">
                          <span className="dc-grading-type-badge dc-grading-type-neutral text-xs">
                            {sp.name}
                            <button type="button" className={`${btnLink} ml-1 text-[10px]`} onClick={() => openDrawer({ type: 'specialization', mode: 'edit', id: sp.id })}>✎</button>
                          </span>
                          <AdminDevRowMeta parts={[`specializations.id=${sp.id}`, `specializations.family_id=${sp.family_id}`]} />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="dc-grading-tree">
                  {familyRoleClusters.map((rc) => {
                    const rcKey = `rc-${rc.id}`;
                    const expanded = expandedNodes.has(rcKey);
                    const rcRoles = roles.filter((r) => r.role_cluster_id === rc.id);
                    const qCount = countQuestionsForRoleCluster(rc.id, rc.family_id, factors, questions);
                    const selected = treeSelection?.kind === 'role_cluster' && treeSelection.id === rc.id;
                    return (
                      <div key={rc.id} className="dc-grading-tree-node">
                        <div className={`dc-grading-tree-row ${selected ? 'dc-grading-tree-row-selected' : ''}`}>
                          <button type="button" className="dc-grading-tree-toggle" onClick={() => toggleNode(rcKey)} aria-expanded={expanded}>
                            <i className={`fas fa-chevron-${expanded ? 'down' : 'right'} text-[10px]`} />
                          </button>
                          <button type="button" className="dc-grading-tree-label" onClick={() => setTreeSelection({ kind: 'role_cluster', id: rc.id })}>
                            <span className="font-medium">{rc.name}</span>
                            <AdminDevRowMeta parts={[`role_clusters.id=${rc.id}`]} />
                            <GradingTypeBadge label="Role cluster" variant="calibration" />
                          </button>
                          <span className="dc-grading-tree-meta">{qCount} вопр.</span>
                          <button
                            type="button"
                            className={btnLink}
                            title="Редактировать Role cluster"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDrawer({ type: 'role_cluster', mode: 'edit', id: rc.id });
                            }}
                          >
                            ✎
                          </button>
                        </div>
                        {expanded && (
                          <div className="dc-grading-tree-children">
                            {rcRoles.map((r) => {
                              const rSelected = treeSelection?.kind === 'role' && treeSelection.id === r.id;
                              const sp = r.specialization_id ? specializations.find((s) => s.id === r.specialization_id) : undefined;
                              return (
                                <div key={r.id} className={`dc-grading-tree-row dc-grading-tree-row-role ${rSelected ? 'dc-grading-tree-row-selected' : ''}`}>
                                  <span className="dc-grading-tree-indent" />
                                  <button
                                    type="button"
                                    className={`${btnLink} dc-grading-tree-edit`}
                                    title="Редактировать Role"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDrawer({ type: 'role', mode: 'edit', id: r.id });
                                    }}
                                  >
                                    ✎
                                  </button>
                                  <button type="button" className="dc-grading-tree-label" onClick={() => setTreeSelection({ kind: 'role', id: r.id })}>
                                    <span>{r.name}, грейд {r.grade_level}</span>
                                    {sp && <span className="text-xs text-slate-500">({sp.name})</span>}
                                    <AdminDevRowMeta parts={[`roles.id=${r.id}`]} />
                                    <GradingTypeBadge label="Role" variant="scoring" />
                                    <GradingScoreRange min={r.min_score} max={r.max_score} />
                                  </button>
                                </div>
                              );
                            })}
                            {rcRoles.length === 0 && <p className="dc-grading-tree-empty">Нет roles</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="dc-grading-col">
          <GradingColumnHeader title="Детали" />
          <div className="dc-grading-col-body">{renderDetails()}</div>
        </section>
      </div>
    </div>
  );
}
